import React, { useEffect, useMemo, useRef, useState } from "react";
import { INiche } from "@/common/services/types";
import { apiNiches } from "@/common/services";
import { NichePreview } from "./NichePreview";
import { useFollowNicheQueries } from "@/common/hooks/useFollowNicheQueries";

interface INichePreviewContainer {
  niche: INiche;
  following: boolean;
  onFollow: (id: string, status: boolean) => void;
  isLink?: boolean;
  withPreview?: boolean;
  visibility?: boolean;
  onPreviewClick?: () => void;
}

export const NichePreviewContainer: React.FC<INichePreviewContainer> = ({
  niche,
  following,
  onFollow: sendFollowState,
  withPreview,
  isLink,
  visibility = false,
  onPreviewClick,
}) => {
  const { onFollowAction } = useFollowNicheQueries({ nicheId: niche.id, following, sendFollowState });
  const [isVisible, setVisible] = useState<boolean>(visibility);
  const previewNicheRef = useRef<HTMLDivElement>(null);

  const onError = async (gif: string) => {
    try {
      await apiNiches.reportBrokenThumbnail({
        niche: niche.id,
        gif: gif,
      });
      console.debug(`Broken preview reported for niche=${niche.id} gif=${gif}`);
    } catch (err) {
      console.warn(`Broken preview could not be reported for niche=${niche.id} gif=${gif}`);
    }
  };

  const observer = useMemo(() => {
    return new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisible(true);
      }
    }, {
      rootMargin: "200px",
    });
  }, []);

  useEffect(() => {
    if (previewNicheRef.current && !visibility) {
      observer.observe(previewNicheRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [observer]);

  return (
    <NichePreview
      niche={niche}
      following={following}
      isLink={isLink}
      previewNicheRef={previewNicheRef}
      withPreview={withPreview}
      isVisible={isVisible}
      onFollow={onFollowAction}
      onError={onError}
      onPreviewClick={onPreviewClick}
    />
  );
};
